import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling callout interactions
 */
export default class Callouts extends MessageBus {
  /**
   * Constructor function of the Callouts class
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * then create the event listeners
   * @returns {object} Callouts A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable().observeCallouts();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} Callouts A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.flagshipWraps = this.element.querySelectorAll(
      `.${CLASS_NAMES.FLAGSHIP_WRAPS}`
    );
    this.iconCalloutIcons = this.element.querySelectorAll(
      `.${CLASS_NAMES.ICON_CALLOUT_ICONS}`
    );
    this.iconCalloutLinks = this.element.querySelectorAll(
      `.${CLASS_NAMES.ICON_CALLOUT_LINKS}`
    );
    this.calloutWraps = this.element.querySelectorAll(
      `.${CLASS_NAMES.ICON_CALLOUT_WRAP}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleFlagshipHover = this.handleFlagshipHover.bind(this);
    this.handleCalloutHover = this.handleCalloutHover.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);
    return this;
  }

  /**
   * Enable event listeners
   * @returns {object} Callouts A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.flagshipWraps.forEach((wrap) => {
      wrap.addEventListener(EVENTS.MOUSEENTER, this.handleFlagshipHover);
      wrap.addEventListener(EVENTS.MOUSELEAVE, this.handleFlagshipHover);
    });

    this.iconCalloutIcons.forEach((icon) => {
      icon.addEventListener(EVENTS.MOUSEENTER, this.handleCalloutHover);
      icon.addEventListener(EVENTS.MOUSELEAVE, this.handleCalloutHover);
    });

    this.iconCalloutLinks.forEach((link) => {
      link.addEventListener(EVENTS.MOUSEENTER, this.handleCalloutHover);
      link.addEventListener(EVENTS.MOUSELEAVE, this.handleCalloutHover);
    });

    return this;
  }

  observeCallouts() {
    const observerOptions = {
      root: null,
      threshold: 0.2
    };

    this.observer = new IntersectionObserver(
      this.handleIntersect,
      observerOptions
    );
    this.calloutWraps.forEach((wrap, index) => {
      const el = wrap;
      el.style.transitionDelay = `${index * 300}ms`;
      this.observer.observe(el);
    });

    return this;
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target); // animate once
      }
    });
  }

  /**
   * Handles hover events for flagship elements
   * @returns {object} Callouts A reference to the current instance of the class
   * @param {Event} event - The event object
   */
  handleFlagshipHover(event) {
    const icon = event.currentTarget.querySelector(
      `.${CLASS_NAMES.FLAGSHIP_ICON}`
    );
    if (icon) {
      icon.classList.toggle('hover', event.type === EVENTS.MOUSEENTER);
    }
    return this;
  }

  /**
   * Handles hover events for callout elements
   * @returns {object} Callouts A reference to the current instance of the class
   * @param {Event} event - The event object
   */
  handleCalloutHover(event) {
    const wrap = event.currentTarget.closest(
      `.${CLASS_NAMES.ICON_CALLOUT_WRAP}`
    );
    if (wrap) {
      const icon = wrap.querySelector(`.${CLASS_NAMES.ICON_CALLOUT_ICON}`);
      if (icon) {
        icon.classList.toggle('hover', event.type === EVENTS.MOUSEENTER);
      }
    }
    return this;
  }
}
